/**
 * Hook: Admin-Badge (Anzahl Kunden mit ungelesenen Nachrichten)
 *
 * Fetcht initial via REST, hört auf WS-Events um Live zu inkrementieren/dekremtieren.
 *
 * Definition (User-Anweisung):
 *   "Wenn ein Kunde 5 Nachrichten geschrieben hat, zählt das als 1.
 *    Wenn 3 Kunden geschrieben haben, zählt als 3."
 *
 * Daher: wir tracken NICHT einzelne Nachrichten, sondern eindeutige Konversationen
 * mit unread_count_admin > 0. Die Wahrheit lebt im Backend (badge-Endpoint),
 * WS-Events triggern nur Re-Fetches statt lokal zu zählen — das vermeidet
 * Drift bei Multi-Tab-Konkurrenz.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchAdminBadge } from '../services/support/adminChatService';
import { useChatWebSocket } from './useChatWebSocket';

interface UseAdminBadgeOptions {
  enabled?: boolean;
}

interface UseAdminBadgeResult {
  count: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useAdminBadge(options: UseAdminBadgeOptions = {}): UseAdminBadgeResult {
  const enabled = options.enabled !== false;
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { subscribe, sendClientEvent } = useChatWebSocket({ enabled });

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    try {
      const next = await fetchAdminBadge();
      setCount(next);
    } catch {
      /* still ok — Badge ist Best-Effort, beim nächsten Polling oder Event */
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Initial
  useEffect(() => {
    if (!enabled) {
      setCount(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void refresh();
  }, [enabled, refresh]);

  // WS-Events triggern Re-Fetch (Backend ist Single Source of Truth)
  // PLUS: delivered-ACK an Server senden, damit der User ✓✓-Häkchen sieht.
  // Dieser Hook ist auch dann aktiv, wenn der Admin die Inbox-Page NICHT
  // offen hat (z.B. Dashboard angeschaut) — daher der richtige Ort für
  // "Frame ist im Browser".
  useEffect(() => {
    if (!enabled) return;
    const unsubNew = subscribe('message:new', (event) => {
      if (event.message.senderType === 'user') {
        // Nur User-Nachrichten ändern den Admin-Badge
        void refresh();
        sendClientEvent({ type: 'mark_delivered', conversationId: event.conversationId });
      }
    });
    const unsubRead = subscribe('message:read', (event) => {
      // Wenn Admin liest: Badge wahrscheinlich runter
      if (event.readByType === 'admin') {
        void refresh();
      }
    });
    return () => {
      unsubNew();
      unsubRead();
    };
  }, [enabled, subscribe, refresh, sendClientEvent]);

  return { count, isLoading, refresh };
}
